import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { onboardingAPI } from '../../services/api';

const goalsActivitiesSchema = z.object({
  height: z.string().min(1, 'Height is required'),
  weight: z.string().min(1, 'Weight is required'),
  shortTermGoals: z.string().min(1, 'Short term goals are required'),
  longTermGoals: z.string().min(1, 'Long term goals are required'),
  doYouExercise: z.enum(['Yes', 'No'], { required_error: 'Please select an option' }),
  currentProgram: z.string().optional(),
  daysPerWeek: z.enum(['1', '2', '3', '4', '5', '6', '7'], { required_error: 'Please select days per week' }),
  timePerWorkout: z.enum(['30 min', '45 min', '60 min', 'More than 60'], { required_error: 'Please select time per workout' }),
  gymName: z.string().optional()
});

type GoalsActivitiesFormData = z.infer<typeof goalsActivitiesSchema>;

interface GoalsActivitiesFormProps {
  onComplete: () => void;
}

const GoalsActivitiesForm: React.FC<GoalsActivitiesFormProps> = ({ onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<GoalsActivitiesFormData>({
    resolver: zodResolver(goalsActivitiesSchema)
  });

  const doYouExercise = watch('doYouExercise');

  const onSubmit = async (data: GoalsActivitiesFormData) => {
    setIsSubmitting(true);
    try {
      await onboardingAPI.submitGoalsActivities(data);
      onComplete();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit goals form');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white shadow rounded-lg p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Goals & Activities</h1>
          <p className="text-red-600 font-medium">**FIELDS ARE REQUIRED</p>
          <p className="text-gray-600 mt-2">
            Help us understand your current fitness level and goals so we can create a personalized program for you.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="height" className="block text-sm font-medium text-gray-700 mb-2">
                **Height
              </label>
              <input
                {...register('height')}
                type="text"
                id="height"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.height ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., 5'10&quot; or 178cm"
                required
              />
              {errors.height && (
                <p className="mt-1 text-sm text-red-600">{errors.height.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="weight" className="block text-sm font-medium text-gray-700 mb-2">
                **Weight
              </label>
              <input
                {...register('weight')}
                type="text"
                id="weight"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.weight ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., 150 lbs or 68 kg"
                required
              />
              {errors.weight && (
                <p className="mt-1 text-sm text-red-600">{errors.weight.message}</p>
              )}
            </div>
          </div>

          {/* Goals */}
          <div className="space-y-4">
            <div>
              <label htmlFor="shortTermGoals" className="block text-sm font-medium text-gray-700 mb-2">
                **Short Term Goals (3-6 months)
              </label>
              <textarea
                {...register('shortTermGoals')}
                id="shortTermGoals"
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.shortTermGoals ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="What would you like to achieve in the next 3-6 months?"
                required
              />
              {errors.shortTermGoals && (
                <p className="mt-1 text-sm text-red-600">{errors.shortTermGoals.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="longTermGoals" className="block text-sm font-medium text-gray-700 mb-2">
                **Long Term Goals (6+ months)
              </label>
              <textarea
                {...register('longTermGoals')}
                id="longTermGoals"
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.longTermGoals ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="What are your long-term fitness and health goals?"
                required
              />
              {errors.longTermGoals && (
                <p className="mt-1 text-sm text-red-600">{errors.longTermGoals.message}</p>
              )}
            </div>
          </div>

          {/* Exercise History */}
          <div className="space-y-4">
            <div>
              <fieldset>
                <legend className="text-sm font-medium text-gray-900 mb-3">
                  **Do you currently exercise?
                </legend>
                <div className="flex space-x-6">
                  <label className="flex items-center">
                    <input
                      {...register('doYouExercise')}
                      type="radio"
                      value="Yes"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      {...register('doYouExercise')}
                      type="radio"
                      value="No"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">No</span>
                  </label>
                </div>
                {errors.doYouExercise && (
                  <p className="mt-1 text-sm text-red-600">{errors.doYouExercise.message}</p>
                )}
              </fieldset>
            </div>

            {doYouExercise === 'Yes' && (
              <div>
                <label htmlFor="currentProgram" className="block text-sm font-medium text-gray-700 mb-2">
                  Current Program (Optional)
                </label>
                <input
                  {...register('currentProgram')}
                  type="text"
                  id="currentProgram"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="What type of exercise program are you currently following?"
                />
              </div>
            )}
          </div>

          {/* Availability */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="daysPerWeek" className="block text-sm font-medium text-gray-700 mb-2">
                **How many days per week can you work out?
              </label>
              <select
                {...register('daysPerWeek')}
                id="daysPerWeek"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.daysPerWeek ? 'border-red-300' : 'border-gray-300'
                }`}
                required
              >
                <option value="">Select days per week</option>
                <option value="1">1 day</option>
                <option value="2">2 days</option>
                <option value="3">3 days</option>
                <option value="4">4 days</option>
                <option value="5">5 days</option>
                <option value="6">6 days</option>
                <option value="7">7 days</option>
              </select>
              {errors.daysPerWeek && (
                <p className="mt-1 text-sm text-red-600">{errors.daysPerWeek.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="timePerWorkout" className="block text-sm font-medium text-gray-700 mb-2">
                **How much time can you dedicate per workout?
              </label>
              <select
                {...register('timePerWorkout')}
                id="timePerWorkout"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.timePerWorkout ? 'border-red-300' : 'border-gray-300'
                }`}
                required
              >
                <option value="">Select time per workout</option>
                <option value="30 min">30 minutes</option>
                <option value="45 min">45 minutes</option>
                <option value="60 min">60 minutes</option>
                <option value="More than 60">More than 60 minutes</option>
              </select>
              {errors.timePerWorkout && (
                <p className="mt-1 text-sm text-red-600">{errors.timePerWorkout.message}</p>
              )}
            </div>
          </div>

          {/* Gym Information */}
          <div>
            <label htmlFor="gymName" className="block text-sm font-medium text-gray-700 mb-2">
              Gym Name (Optional)
            </label>
            <input
              {...register('gymName')}
              type="text"
              id="gymName"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="What gym do you go to?"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Continue >'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoalsActivitiesForm;
